import { Box, IconButton, Typography } from '@mui/material';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { useBudgetWindow } from '../../store/Budget/BudgetContext';
import { BUDGET_WINDOW_SIZE, formatMonthRange, isBudgetRangeActive } from './helpers';
import dayjs from '@utils/dayjs';

/** CALL-815: what an active `Datum von`–`bis` range does to this control.
 *
 *  - `ignore` — the year navigation, range or not. The DEFAULT, because the range is an OVERVIEW filter
 *    while this control is on three pages: it lives in BudgetWindowContext, which BudgetLayout mounts
 *    around every budget route, so it deliberately survives a trip to Prices & ABT and back. That page's
 *    grids render the 12 window columns whatever range is set, and it has no range control to clear the
 *    range with — a range-aware default disabled all six of its arrows with no way back short of returning
 *    to the Overview. Defaulting to `ignore` keeps a future call site safe until it renders a range control.
 *  - `readout` — the period replaces the year label and both arrows go inert, because the range, not the
 *    window, is what the grids below show. For the ONE control that names the period: the Overview's first
 *    section heading, "Ist Budget".
 *  - `hide` — nothing is rendered while a range is applied. For the sections below it, whose grids do follow
 *    the range but whose copy of the period would only repeat what that first heading already says. */
type RangeBehaviour = 'ignore' | 'readout' | 'hide';

interface MonthWindowNavProps {
    rangeBehaviour?: RangeBehaviour;
}

// CALL-745 follow-up / CALL-772: linked window navigation shared by every budget month grid. The
// window is one calendar year (Jan–Dec); ◀/▶ move it a whole year at a time. The window lives in
// BudgetContext, so operating the control on any grid moves all grids on the page in sync.
const MonthWindowNav = ({ rangeBehaviour = 'ignore' }: MonthWindowNavProps) => {
    const { windowStart, shiftWindow, rangeFrom, rangeTo } = useBudgetWindow();
    // CALL-815: on the Overview an active `Datum von`–`bis` range replaces the window as the view, so this
    // control has nothing left to move. `readout` keeps it on screen with its arrows DISABLED — it is then
    // the caption naming the months the grids show — while `hide` drops it, because the first section
    // heading already carries that caption and three copies of it read as three separate periods.
    const rangeActive = rangeBehaviour !== 'ignore' && isBudgetRangeActive(rangeFrom, rangeTo);
    if (rangeActive && rangeBehaviour === 'hide') return null;
    // CALL-772: windowStart is Jan-aligned (default Jan, version-change reset to Jan, and the ±year
    // shifts below preserve it), so the visible window is exactly one calendar year — label it with
    // that year. `notranslate` keeps browser translation extensions from rewriting the number in
    // place (the CALL-759 reconciliation crash) as it changes on navigation.
    const label = rangeActive ? formatMonthRange(rangeFrom, rangeTo) : dayjs(windowStart).format('YYYY');

    // Relative shifts go through the context's functional updater, so clicking faster than the grids
    // re-render never drops a click (each advances from the latest committed window). A year is
    // BUDGET_WINDOW_SIZE (12) months.
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
            <IconButton
                size="small"
                aria-label="previous year"
                disabled={rangeActive}
                onClick={() => shiftWindow(-BUDGET_WINDOW_SIZE)}
            >
                <KeyboardArrowLeftIcon fontSize="small" />
            </IconButton>
            <Typography
                variant="body2"
                sx={{ minWidth: 64, textAlign: 'center', fontWeight: 600, whiteSpace: 'nowrap' }}
            >
                <span className="notranslate">{label}</span>
            </Typography>
            <IconButton
                size="small"
                aria-label="next year"
                disabled={rangeActive}
                onClick={() => shiftWindow(BUDGET_WINDOW_SIZE)}
            >
                <KeyboardArrowRightIcon fontSize="small" />
            </IconButton>
        </Box>
    );
};

export default MonthWindowNav;
